<script>

import { page } from '$app/stores';
import { get } from 'svelte/store';
import { is_natural } from '$lib/universal/lib.js';

let {
    current_page = 1,               // 1 ...
    total_pages = undefined,        // last page if available or undefined
    important_page = undefined,     // important_page, if any
    url_param = false,              // if true ?page=33, otherwise /33/,
    show_last_page = false,
    nofollow = false
} = $props();

let pages = $derived.by(() => generatePagination({current_page, total_pages, important_page, show_last_page}));

function generatePagination({
        length = 8,
        current_page,
        total_pages,
        important_page,
        show_last_page = false
    } = {}) {

    if (length <= 0) return []; // No pagination elements if length <= 0

    // Natural Numbers are ok,
    // natural numbers as strings are converted to Numbers,
    // otherwise sets default or undefined:
    current_page = checkParam(current_page, 1);  // 1 ...
    total_pages = total_pages === 0 ? 1 : checkParam(total_pages);       // last page if known, or undefined
    important_page = checkParam(important_page); // important_page, if any
    current_page = checkParam(current_page, 1);  // 1 ... total_pages

    const pagesSet = new Set(); // To track added pages and avoid duplicates

    function addPage(pageNo) {
        // Check for maximum pagination block length (with ellipses)
        if (pagesSet.size + findEllipses(pagesSet, 1, total_pages).size  < length) {
            pagesSet.add(pageNo);
            return pageNo;
        }
        return undefined;
    }

    if (current_page !== undefined) addPage(current_page);

    if (important_page !== undefined) addPage(important_page);

    // Add a page next to the current_page (NC == next to current)
    let nc;
    if (current_page !== undefined && current_page + 1 <= (total_pages || Infinity)) {
        nc = addPage(current_page + 1);
    }

    addPage(1);

    // Add the last page if show_last_page is true
    if (show_last_page && total_pages !== undefined) {
        addPage(total_pages);
    }

    // Add a page before the current_page (BC == before current)
    let bc;
    if (current_page !== undefined && current_page - 1 >= 1) {
        bc = addPage(current_page - 1);
    }

    // Add a page next to the first page (NF == next to first)
    let nf;
    if (pagesSet.has(1) && total_pages > 1) {
        nf = addPage(2);
    }

    while (pagesSet.size < length) {
        const sizeBefore = pagesSet.size;
        if (nc && nc < (total_pages || Infinity)) {
            addPage(++nc);
        }
        if (bc && bc > 1) {
            addPage(--bc);
        }
        if (nf && nf < (total_pages || Infinity)) {
            addPage(++nf);
        }
        if(sizeBefore == pagesSet.size) break;
    }

    const ellipsesSet = findEllipses(pagesSet, 1, total_pages);

    // .union() method have just arrived to browsers and not yet to Node 20.5.1 on Ubuntu 24.04.1 LTS
    //const sortedSet = Array.from(pagesSet.union(ellipsesSet)).sort((a, b) => a - b);
    for (const i of ellipsesSet) {
        pagesSet.add(i);
    }

    const sortedSet = Array.from(pagesSet).sort((a, b) => a - b);
    const result = [];

    for (let i = 0; i < sortedSet.length; ++i) {
        let page = sortedSet[i];
        let nextPage = { page };
        if(ellipsesSet.has(page)
            && page !== 1  // Avoid ellipses which cover gaps of only 1 page
            && page !== total_pages
            && !(sortedSet[i-1] === page-1 && sortedSet[i+1] === page+1)) {
                nextPage.ellipsis = true;
        }
        result.push(nextPage);
    }

    return result;
}

// Param must be is_natural (1, 2, "1", 5, "33"...) or return the_default
function checkParam(param, the_default = undefined) {
    return is_natural(param) ? parseInt(param, 10) : the_default;
}

function findEllipses(sparseSet, lowerBound, upperBound) {
    // Sort the sparse set and remove duplicates
    const sortedSet = Array.from(sparseSet).sort((a, b) => a - b);
    const ellipsesSet = new Set();

    // Check for omissions before the first number in the set
    if (sortedSet[0] > lowerBound) {
        ellipsesSet.add(sortedSet[0] - 1);
    }

    // Check for omissions between numbers in the sparse set
    for (let i = 0; i < sortedSet.length - 1; i++) {
        const current = sortedSet[i];
        const next = sortedSet[i + 1];
        if (next > current + 1) {
            ellipsesSet.add(current + 1);
        }
    }

    // Check for omissions after the last number in the set
    if (!upperBound || sortedSet[sortedSet.length - 1] < upperBound) {
        ellipsesSet.add(sortedSet[sortedSet.length - 1] + 1);
    }

    //console.log('sortedSet='+JSON.stringify(Array.from(sortedSet)));
    //console.log('ellipsesSet='+JSON.stringify(Array.from(ellipsesSet)));
    return ellipsesSet;
}

function makePageHref(pageNo, unused) {
    if(url_param) {
        let urlCopy = new URL(get(page).url.toString());
        urlCopy.searchParams.set('page', pageNo);
        return urlCopy.toString();
    } else {
        return get(page).url.pathname.replace(/(\/[^\/]+\/?)$/, `/${pageNo}`);
    }
}

</script>

<nav class="pagination" aria-label="pagination">
    <ul class="pagination-list ml-0">
        {#each pages as P}
            {#if !P?.ellipsis}
                <li class="mt-0">
                    <a href={makePageHref(P.page, $page)}
                        class="pagination-link {P.page == current_page ? 'is-current' : ''}"
                        class:is-important={P.page == important_page && !(P.page == current_page)}
                        aria-label="Page {P.page}"
                        aria-current="page"
                        data-sveltekit-replacestate
                        rel={nofollow ? 'nofollow' : ''}
                    >{P.page}</a>
                </li>
            {:else}
                <li class="mt-0">
                    <a href={makePageHref(P.page, $page)}
                        class="pagination-ellipsis clickable-ellipsis"
                        data-sveltekit-replacestate
                    >&hellip;</a>
                </li>
            {/if}
        {/each}
    </ul>
</nav>

<style>
.clickable-ellipsis { 
    pointer-events: all; 
    cursor: pointer 
}

.is-important {
    background: #EBFFDE !important;
}
@media (prefers-color-scheme: dark) {
    .is-important {
        background: rgb(50, 60, 80) !important;
    }
}
</style>
